'use client';

import React from 'react';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import BgMedia from '../backgrounds/bg_media';
import { render } from 'storyblok-rich-text-react-renderer';
import useHideOnSite from '../../../hooks/useLanguageFilter';
import { SectionWrapper } from './sectionWrapper';
/**
 * Component representing a section in the application.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.blok - The blok object.
 * @param {Object} props.hotel - The hotel object.
 * @param {number} props.price - The price value.
 * @param {string} props.season - The season string.
 * @param {string} props.destination - The destination string.
 * @param {string} props.currentStoryInternalId - The current story internal ID.
 * @param {boolean} [props.isInSection=false] - Indicates if the section is in the current section.
 * @param {boolean} [props.onlySectionsAsSiblings=false] - Indicates if only sections are used as siblings.
 * @param {boolean} [props.hasHorizontalScroll=false] - Indicates if the section has horizontal scroll.
 * @param {boolean} [props.isLast=false] - Indicates if the section is the last one.
 * @returns {JSX.Element} The rendered section component.
 */

const Section = ({
  blok,
  hotel,
  price,
  priceType,
  season,
  destination,
  isInSection = false,
  onlySectionsAsSiblings = false,
  hasHorizontalScroll = false,
  parentWidth,
}) => {
  // Handling of Section when scroll activated --- --- --- --- --->

  // Tailwind utility classes --- --- --- --- --- --->

  // Define background color classes
  const backgroundColors = {
    yellow: 'bg-yellow-15',
    'yellow-announcement': 'bg-yellow',
    blue: 'bg-blue-7.5',
    'blue-announcement': 'bg-blue text-white',
    'blue-secondary': 'bg-blue-15',
    green: 'bg-green-7.5',
    'green-announcement': 'bg-green text-white',
    'green-secondary': 'bg-green-15',
    orange: 'bg-orange-7.5',
    'orange-announcement': 'bg-orange text-white',
    'orange-secondary': 'bg-orange-15',
    black: 'bg-black text-white',
    transparent: 'bg-transparent',
  };

  // Apply background color class based on blok.background_color
  const backgroundColor = backgroundColors[blok.background_color] || '';

  // Define column width classes
  /*
   ** The conditional widths are due to "Section" in some cases being used similarily as a "Card" component (e.g. Start / Skidresor / Österrike -> Våra destinationer).
   */

  const columnWidths =
    isInSection && onlySectionsAsSiblings && hasHorizontalScroll
      ? {
          'w-full': 'col-span-12',
          'w-3/4': 'col-span-12 md:col-span-9',
          'w-2/3': 'col-span-12 md:col-span-8',
          'w-1/2': 'col-span-12 md:col-span-6',
          'w-1/3': 'col-span-12 md:col-span-6 lg:col-span-5 xl:col-span-4',
          'w-1/4': 'col-span-6 md:col-span-3',
          auto: 'w-fit',
        }
      : {
          'w-full': 'col-span-12',
          'w-3/4': 'col-span-12 md:col-span-6 lg:col-span-9',
          'w-2/3': 'col-span-12 md:col-span-6 lg:col-span-8',
          'w-1/2': 'col-span-12 md:col-span-6',
          'w-1/3': 'col-span-12 md:col-span-6 lg:col-span-4',
          'w-1/4': 'col-span-12 md:col-span-3',
          auto: 'w-auto',
        };

  // Apply column width class based on blok.width
  const width = columnWidths[blok.width] || 'col-span-12';

  const parentwidth = isInSection ? blok.width : parentWidth;

  const storyblokHeight = {
    sectionHeightAuto: 'h-auto',
    sectionHeightContent: 'h-fit',
  };

  const height = storyblokHeight[blok.height] || '';

  // Define top margin classes
  const topMargins = {
    tiny_variable: 'mt-2 md:mt-4',
    small: 'mt-6',
    medium: 'mt-8',
    large: 'mt-12',
    none: 'mt-0',
  };

  // Apply top margin class based on blok.margin_top
  const topMargin = topMargins[blok.margin_top] || '';

  // >--- --- --- --- --- --- Tailwind utility classes

  // Function to render rich text content
  const RichText = ({ document }) => {
    return render(document, {
      // Custom resolver for Storyblok components
      defaultBlokResolver: (name, props) => {
        const blok = { ...props, component: name };
        return (
          <StoryblokComponent
            blok={blok}
            key={blok._uid}
            destination={destination}
            season={season}
            hotel={hotel}
            price={price}
            priceType={priceType}
            inTextBlock={true}
            parentColor={blok.background_color}
          />
        );
      },
    });
  };

  // Render the preamble using the RichText component
  const preamble = RichText({ document: blok.section_preamble });

  // >--- --- --- Additional props for nested components

  // Optional background image or video --- --- --- --- --->
  const backgroundMedia = blok.image_or_video_background || '';
  const mediaParams = {
    backgroundMedia: blok.image_or_video_background || {},
    isLooping: blok.loop_background_video || false,
    parentWidth: parentwidth,
    imageId: blok.qbank_id,
  };

  const showTitleBlock = () => {
    if (
      (blok.section_title && blok.section_title.length > 0) ||
      (blok.section_title_link_button &&
        blok.section_title_link_button.length > 0)
    ) {
      return true;
    } else return false;
  };

  return useHideOnSite(blok.hide_on_site) ? null : (
    // Render the section container
    <section
      className={`flex flex-wrap scroll-mt-12 ${
        blok.horizontal_scroll ? 'flex-col' : ''
      } justify-center ${width} ${height} ${backgroundColor} ${topMargin} `}
      {...storyblokEditable(blok)}
      id={blok.name}
    >
      {showTitleBlock() && (
        <div className="flex w-full relative">
          {/* Render the section title if it exists */}
          {blok.section_title && blok.section_title.length > 0 && (
            <h1 className="px-4 md:px-8 mt-12 mb-4 text-lg md:text-xl">
              {blok.section_title}
              {/* Render the section title link button if it exists */}
              {blok.section_title_link_button &&
                blok.section_title_link_button.length > 0 && (
                  <span className="absolute top-9 inline-flex items-baseline ml-2 md:ml-4">
                    <StoryblokComponent
                      blok={blok.section_title_link_button[0]}
                    />
                  </span>
                )}
            </h1>
          )}
        </div>
      )}
      {/* Render the section preamble if it exists */}
      {preamble && preamble.length > 0 && (
        <div className="w-full px-4 md:px-8">{preamble}</div>
      )}

      <div className="w-full relative">
        {/* Render the background media if it exists */}
        {(!blok.background_slider || blok.background_slider.length === 0) &&
          (backgroundMedia.filename || mediaParams.imageId) && (
            <BgMedia mediaParams={mediaParams} />
          )}
        {/* Render the blocks */}

        <SectionWrapper
          blok={blok}
          destination={destination}
          season={season}
          hotel={hotel}
          price={price}
          priceType={priceType}
          width={width}
          parentWidth={parentwidth}
        />
      </div>
    </section>
  );
};

export default Section;
